import { makeAutoObservable } from "mobx";
import { CUSTOMER_ACTIONS, ICancelInactiveRmasSettings } from "../models/User";
import { IChangeable } from "./types";
import { toNullableNumber } from "../models/model-types-methods";


enum CANCEL_INACTIVE_RMA_SETTINGS_TRANSLATION_KEYS {
    FEE_INVOICE_NOT_PAYED_CANCEL_REASON = 'FEE_INVOICE_NOT_PAYED_CANCEL_REASON',
    EXCHANGE_INVOICE_NOT_PAYED_CANCEL_REASON = 'EXCHANGE_INVOICE_NOT_PAYED_CANCEL_REASON',
    NOT_SHIPPED_BACK_CANCEL_REASON = 'NOT_SHIPPED_BACK_CANCEL_REASON',
}

interface IRefundToBankSettingChanges extends Partial<ICancelInactiveRmasSettings> {}


class CancelInactiveRmasSettingStore implements IChangeable<ICancelInactiveRmasSettings>{ 
    private _model?: ICancelInactiveRmasSettings;
    private _changes: IRefundToBankSettingChanges = {};

    constructor(private _customerAction: CUSTOMER_ACTIONS, model?: ICancelInactiveRmasSettings) {
        makeAutoObservable(this, {}, { autoBind: true });
        if (model) {
            this._setModel(model);
        }
    }

    public get customerAction(): CUSTOMER_ACTIONS { return this._customerAction; }
    public get reason(): string { 
        if (this.customerAction === CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE) {
            return CANCEL_INACTIVE_RMA_SETTINGS_TRANSLATION_KEYS.FEE_INVOICE_NOT_PAYED_CANCEL_REASON;
        }

        if (this.customerAction === CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE) {
            return CANCEL_INACTIVE_RMA_SETTINGS_TRANSLATION_KEYS.EXCHANGE_INVOICE_NOT_PAYED_CANCEL_REASON;
        }

        return CANCEL_INACTIVE_RMA_SETTINGS_TRANSLATION_KEYS.NOT_SHIPPED_BACK_CANCEL_REASON;
    }
    
    public get cancelAfterDays(): number|null { 
        if (this._changes.cancel_after_days === null) {
            return null;
        }
        return this._changes.cancel_after_days ?? this._model?.cancel_after_days ?? null; 
    }

    public setCancelAfterDays(v: number|null) {
        this._changes.cancel_after_days = v;
        this._clearUnneededChanges();
    }

    private _setModel(model: ICancelInactiveRmasSettings): void {
        this._model = ensureValidModel(model);
    }

    hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    discardChanges(): void {
        this._changes = {};
    }
    
    getUpdatedModel(): ICancelInactiveRmasSettings|undefined {
        if (!this._model?.id && !this.hasChanges()) {
            return undefined;
        }
        
        const updatedModel = { 
            ...this._model, 
            ...this._changes, 
            customer_action: this._customerAction,
            reason: this.reason,
            _has_changes: !!this.hasChanges() 
        } as ICancelInactiveRmasSettings;
        
        return updatedModel;
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IRefundToBankSettingChanges;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

}

export default CancelInactiveRmasSettingStore;

function ensureValidModel(data: ICancelInactiveRmasSettings): ICancelInactiveRmasSettings {
    return {
        ...data,
        cancel_after_days: toNullableNumber(data.cancel_after_days)
    };
}